import { Brain, Compass } from "lucide-react";
import type { LucideIcon } from "lucide-react";

/**
 * Single source of truth for the games this app ships.
 *
 * Owner intent: games are PLAYABLE WITHOUT registration by default. Only games
 * explicitly flagged `requiresAuth: true` stay SSO-gated (page-level
 * requireUser → accounts /login) and get a "requires sign-in" tile badge.
 * The locale root (app/[locale]/page.tsx) renders the game-grid hub inline for
 * authed visitors and the marketing landing tiles for anon — both from this
 * catalog (games live at the locale root, not under /games), and the
 * per-game pages read `requiresAuth` here to decide whether to gate.
 */

/** @owlcat/ui gradient Button variants used for the landing tile CTAs. */
export type GameAccent =
  | "gradient-purple"
  | "gradient-blue"
  | "gradient-green"
  | "gradient-pink"
  | "gradient-orange";

export interface GameCatalogEntry {
  id: string;
  /** App-local route (next-intl Link adds the /<locale> prefix). */
  href: string;
  /** i18n key for the display name. */
  name: string;
  /** i18n key for the description. */
  description: string;
  /** Hub tile icon. */
  icon: LucideIcon;
  /** Landing tile emoji chip. */
  emoji: string;
  /** Landing tile CTA gradient. */
  accent: GameAccent;
  isNew?: boolean;
  /**
   * When true the game page stays SSO-gated (requireUser) — anon visitors are
   * redirected to accounts /login — and its tiles show a "sign in required"
   * badge. Default false: the game is public / playable without an account.
   */
  requiresAuth: boolean;
}

export const gamesCatalog: GameCatalogEntry[] = [
  {
    id: "memory",
    href: "/memory",
    name: "games.memory.name",
    description: "games.memory.description",
    icon: Brain,
    emoji: "🧠",
    accent: "gradient-blue",
    isNew: true,
    requiresAuth: false,
  },
  {
    id: "maze",
    href: "/maze",
    name: "games.maze.name",
    description: "games.maze.description",
    icon: Compass,
    emoji: "🧩",
    accent: "gradient-purple",
    requiresAuth: false,
  },
];

/** Look up a single game by id. */
export function getGame(id: string): GameCatalogEntry | undefined {
  return gamesCatalog.find((g) => g.id === id);
}

/** Whether a game's page must stay SSO-gated. Unknown ids default to false. */
export function gameRequiresAuth(id: string): boolean {
  return getGame(id)?.requiresAuth ?? false;
}
